import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CmsService, Page, WindowRef } from '@spartacus/core';
import { Request } from 'express';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { ILink, SdBreadcrumbComponent } from '@seb/web-design-system/core';
import { Bundle, UrlMapping } from '../../../../models';
import { LinkMapper } from '../../../../mappers';
import { BundleService, ConfigurationService, TrackingService, UrlMappingService } from '../../../../services';
import { SERVER_REQUEST } from '../../../../constants';
import { CustomJsonLdDirective } from '../../../../directives';

@Component({
  selector: 'wc-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdBreadcrumbComponent,
    CustomJsonLdDirective
  ]
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
  breadcrumb: ILink[];
  separator: string;
  snippetsBreadCrumb;
  private readonly destroy$: Subject<void> = new Subject();
  @Input() titleRecipe = '';

  constructor(
    private cmsService: CmsService,
    private configService: ConfigurationService,
    private trackingService: TrackingService,
    private readonly route: ActivatedRoute,
    private readonly bundleService: BundleService,
    private readonly urlMappingService: UrlMappingService,
    private readonly cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(SERVER_REQUEST) private request: Request,
    @Optional() private windowRef: WindowRef
  ) {}

  ngOnInit(): void {

    const bundle$: Observable<Bundle | undefined> = this.route?.queryParamMap?.pipe(
      map((param) => param.get('bundle')),
      switchMap((bundleCode) =>
        bundleCode ? this.bundleService.getBundle(bundleCode, 'bundleNameAndUrl') : of(undefined)
      )
    );
    const page$: Observable<Page> = this.cmsService.getCurrentPage().pipe(filter((page) => !!page));
    const config = this.configService.config;
    this.separator = config.components?.breadcrumbs?.separator;
    combineLatest([bundle$, page$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([bundle, page]) => {
        const pageBreadcumbs = Array.isArray(page['breadcrumbs']?.breadcrumb)
          ? page['breadcrumbs'].breadcrumb
          : [page['breadcrumbs']?.breadcrumb];
        const tempBreadcrumbs: ILink[] = pageBreadcumbs.map((breadcrumb) => ({
          linkName: breadcrumb?.name,
          url: breadcrumb?.url === 'homepage' ? '/' : LinkMapper.decodeUrl(breadcrumb?.resolvedUrl || breadcrumb?.url)
        }));
        if (bundle) {
          tempBreadcrumbs.pop();
          tempBreadcrumbs.push({
            linkName: bundle.name,
            url: LinkMapper.decodeUrl(bundle.canonicalURL)
          });
        }
        if (page.label === '/recipe/detail' && !!this.titleRecipe) {
          tempBreadcrumbs.pop();
          tempBreadcrumbs.push({
            linkName: this.titleRecipe
          });
        }
        const linksToTranslate$: Observable<UrlMapping | undefined>[] = tempBreadcrumbs.map((link) => {
          const urlStart = link?.url?.split('/')[1];
          if (urlStart) {
            return this.urlMappingService.getUrlMappingByCode('/' + urlStart);
          } else {
            return of(undefined);
          }
        });
        if (linksToTranslate$.length > 0) {
          combineLatest(linksToTranslate$).subscribe((linksTranslations) => {
            tempBreadcrumbs.forEach((breadbrumb, index) => {
              const urlMapping = linksTranslations[index];
              if (urlMapping) {
                breadbrumb.url = breadbrumb.url.replace(urlMapping.code, urlMapping.value);
              }
            });
            this.breadcrumb = [...tempBreadcrumbs];
            this.snippetsBreadCrumb = this.breadcrumb?.length > 1 ? this.toSnippets(this.breadcrumb) : {};
            this.cdRef.markForCheck();
          });
        } else {
          this.breadcrumb = [...tempBreadcrumbs];
          this.snippetsBreadCrumb = this.breadcrumb?.length > 1 ? this.toSnippets(this.breadcrumb) : {};
          this.cdRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClickLink(event: ILink): void {
    if (event.linkName) {
      const linkNames: string[] = [];
      this.breadcrumb.slice(0, this.breadcrumb.findIndex((v) => event.linkName === v.linkName) + 1).forEach((v) => {
        linkNames.push(v.linkName);
      });
      const navLevel = linkNames.join('/');
      this.trackingService.sendEvent('clickNavigation', { navLevel });
    }
  }
  private toSnippets(breadcrumb: any) {
    const snippets = {
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: this.transformToItemListElement(breadcrumb)

    };
    return snippets;
  }

  private transformToItemListElement(breadcrumbData: any[]) {
    const host = isPlatformServer(this.platformId)
      ? this.request.hostname
      : this.windowRef.nativeWindow.location.hostname;
    const hostUrl = `https://${host}`;
    return breadcrumbData.map((item, index) => {
      return {
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.linkName ?? '',
        'item': item.url ? hostUrl + item.url : ''
      };
    });
  }
}
